// src/api/api.ts
import axios from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import { isTokenExpired } from "../utils/jwtUtils";
import { getLoginUrl } from "../utils/subdomain";
import { getAccessToken as getStoredAccessToken, setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken, clearTokens } from "../utils/tokenStorage";

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const PUBLIC_API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL;
console.log("The api url is", PUBLIC_API_BASE);
// Token management - will be set by AuthContext
let currentToken: string | null = null;
let currentRefreshToken: string | null = null;

// Function to set token (called from AuthContext)
export const setAuthToken = (token: string | null) => {
  currentToken = token;
  console.log('🔐 Auth token updated:', token ? 'Token set' + token : 'Token cleared');

  // Expose token globally for micro-frontends
  if (typeof window !== 'undefined') {
    (window as any).getAuthToken = getAuthToken;
    (window as any).__SHELL_APP_API__ = {
      getAuthToken,
      setAuthToken
    };

    // Notify micro-frontends of token updates
    if ((window as any).__REVIEW_CLOUD_API__?.setAuthToken) {
      console.log('🔄 Shell App: Syncing token to Review Cloud');
      (window as any).__REVIEW_CLOUD_API__.setAuthToken(token);
    }

    // Notify Comp Builder of token updates
    if ((window as any).__COMP_BUILDER_API__?.setAuthToken) {
      console.log('🔄 Shell App: Syncing token to Comp Builder');
      (window as any).__COMP_BUILDER_API__.setAuthToken(token);
    }
  }
};

// Function to set refresh token
export const setRefreshToken = (token: string | null) => {
  currentRefreshToken = token;
  console.log('🔐 Refresh token updated:', token ? 'Token set' : 'Token cleared');
};

// Function to get current token
export const getAuthToken = () => currentToken;

// Function to get current refresh token
export const getRefreshToken = () => currentRefreshToken;

// Track if we're currently refreshing the token to prevent multiple refresh attempts
let isRefreshing = false;
let sessionExpired = false; // Track if session has completely expired
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

// Process the queue of failed requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to refresh the access token
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = currentRefreshToken || localStorage.getItem('refresh_token');

  if (!refreshToken) {
    console.error('❌ No refresh token available');
    return null;
  }

  try {
    console.log('🔄 Proactively refreshing access token...');

    const response = await axios.post(`${PUBLIC_API_BASE}/account/refresh-token`, {
      refresh_token: refreshToken
    }, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    });

    if (response.data && response.data.access_token) {
      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token || refreshToken;

      console.log('✅ Token refreshed proactively');

      // Update tokens
      setAuthToken(newAccessToken);
      setRefreshToken(newRefreshToken);
      setStoredAccessToken(newAccessToken);
      setStoredRefreshToken(newRefreshToken);

      return newAccessToken;
    }

    return null;
  } catch (error: any) {
    console.error('❌ Proactive token refresh failed:', error);

    // If refresh token is expired or invalid, clear auth and redirect to login
    if (error.response?.status === 400 || error.response?.status === 401) {
      console.error('🚫 Refresh token is expired or invalid - clearing auth');

      // Mark session as expired to prevent further requests
      sessionExpired = true;

      // Clear all auth data (cookies + localStorage)
      setAuthToken(null);
      setRefreshToken(null);
      clearTokens();
      localStorage.removeItem('user');
      localStorage.removeItem('account');

      // Redirect to login (only once), unless on 2FA setup page
      setTimeout(() => {
        if (!window.location.pathname.includes('/auth/2fa-setup')) {
          window.location.href = getLoginUrl();
        }
      }, 100);
    }

    return null;
  }
};

// Private (authenticated) API
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/core/api/v1",
  headers: { "Content-Type": "application/json", "Accept": "application/json" },
});

// Request interceptor to add JWT token, impersonation token, and check expiration
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);

    // Check if we're in an impersonation session
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    const isImpersonating = !!impersonationDataStr;

    // If session has expired and NOT impersonating, reject all requests
    if (sessionExpired && !isImpersonating) {
      console.log('🚫 Session expired - blocking request');
      return Promise.reject(new Error('Session expired'));
    }

    // Get current token
    const token = currentToken || getStoredAccessToken();

    if (token) {
      // Skip token expiration check during impersonation - the impersonation token handles auth
      if (!isImpersonating && isTokenExpired(token, 60)) {
        console.log('⏰ Token expired or expiring soon, refreshing proactively...');

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const newToken = await refreshAccessToken();

            if (newToken) {
              config.headers.Authorization = `Bearer ${newToken}`;
              console.log('✅ Request using refreshed token');
              processQueue(null, newToken);
            } else {
              console.warn('⚠️ Token refresh failed, using existing token');
              config.headers.Authorization = `Bearer ${token}`;
            }

            isRefreshing = false;
          } catch (error) {
            isRefreshing = false;
            console.error('❌ Error during proactive token refresh:', error);
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          // If already refreshing, wait for it to complete
          await new Promise((resolve) => {
            failedQueue.push({ resolve, reject: () => { } });
          });
          const newToken = currentToken || getStoredAccessToken();
          config.headers.Authorization = `Bearer ${newToken}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ JWT Token attached to request');
        console.log('📋 Token preview:', token.substring(0, 50) + '...');
      }
    } else {
      console.log('⚠️  No JWT token available');
    }

    // Add impersonation token if active
    if (impersonationDataStr) {
      try {
        const impersonationData = JSON.parse(impersonationDataStr);
        if (impersonationData.sessionToken) {
          console.log('👤 Adding impersonation token to request:', config.url);
          config.headers['X-Impersonation-Token'] = impersonationData.sessionToken;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and token refresh
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    console.log('📦 Response data:', response.data);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if we're in an impersonation session
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    const isImpersonating = !!impersonationDataStr;

    if (error.response?.status === 401 && !originalRequest._retry) {
      console.error('🚫 401 Unauthorized - Attempting token refresh');

      // During impersonation, don't try to refresh token or redirect to login
      // Just reject the error and let the impersonation session handle it
      if (isImpersonating) {
        console.log('👤 401 during impersonation - not triggering logout');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = currentRefreshToken || localStorage.getItem('refresh_token');

      if (!refreshToken) {
        console.error('❌ No refresh token available - redirecting to login');
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);

        // Clear all auth data (cookies + localStorage)
        setAuthToken(null);
        setRefreshToken(null);
        clearTokens();
        localStorage.removeItem('user');
        localStorage.removeItem('account');

        // Don't redirect if user is on 2FA setup page
        if (!window.location.pathname.includes('/auth/2fa-setup')) {
          window.location.href = getLoginUrl();
        }
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshAccessToken();

        if (newAccessToken) {
          console.log('✅ Token refreshed successfully (reactive)');

          // Update the failed request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Process queued requests
          processQueue(null, newAccessToken);
          isRefreshing = false;

          // Retry the original request
          return api(originalRequest);
        } else {
          // Refresh failed - session expired flag already set by refreshAccessToken
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed (reactive):', refreshError);
        isRefreshing = false;
        processQueue(refreshError, null);

        // If not already handled by refreshAccessToken, mark session as expired
        if (!sessionExpired) {
          sessionExpired = true;

          // Clear all auth data (cookies + localStorage)
          setAuthToken(null);
          setRefreshToken(null);
          clearTokens();
          localStorage.removeItem('user');
          localStorage.removeItem('account');

          setTimeout(() => {
            if (!window.location.pathname.includes('/auth/2fa-setup')) {
              window.location.href = getLoginUrl();
            }
          }, 100);
        }

        return Promise.reject(refreshError);
      }
    } else {
      console.error(`❌ API Error: ${error.response?.status || 'Network'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      console.error('📦 Error response:', error.response?.data);
    }
    return Promise.reject(error);
  }
);

// Public API (no auth header)
const public_api = axios.create({
  baseURL: PUBLIC_API_BASE,
  headers: { "Content-Type": "application/json", "Accept": "application/json" },
  timeout: 500000,
});


// Public API interceptors for logging
public_api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log(`🌐 Public API Request: ${config.method?.toUpperCase()} ${fullUrl}`);
    console.log(`   Base URL: ${config.baseURL}`);
    console.log(`   Path: ${config.url}`);
    return config;
  }
);

public_api.interceptors.response.use(
  (response) => {
    console.log(`✅ Public API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error(`❌ Public API Error: ${error.response?.status || 'Network'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
    return Promise.reject(error);
  }
);

export default api;
export { public_api };
